import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, FileText, Plus, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCanEdit } from "@/context/AuthContext";
import { PortButtons } from "@/components/DataPorting";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";

const blankFor = (kind) => ({
  display_name: "", gstin: "", phone: "", email: "", billing_address: "",
  state_code: "27", credit_period_days: 15, price_list_id: "",
  is_customer: kind !== "SUPPLIER",
  is_supplier: kind === "SUPPLIER",
});

const COPY = {
  CUSTOMER: { title: "Customers", noun: "customer", balanceLabel: "Receivable",
              empty: "No customers yet" },
  SUPPLIER: { title: "Suppliers", noun: "supplier", balanceLabel: "Payable",
              empty: "No suppliers yet" },
};

/**
 * One component, two screens. Customers and suppliers are separate lists with
 * separate creation flows, but they share a single record: a business you both
 * buy from and sell to keeps one ledger that nets off, rather than appearing
 * twice with two balances.
 */
export default function Parties({ kind = "CUSTOMER" }) {
  const copy = COPY[kind];
  const blank = blankFor(kind);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [states, setStates] = useState([]);
  const [gstinInfo, setGstinInfo] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const navigate = useNavigate();
  const canEdit = useCanEdit();

  const load = () => api.get("/parties", { params: { q: q || undefined, kind } }).then(({ data }) => setRows(data));

  // `kind` belongs here. /customers and /suppliers render this same component
  // at the same place in the tree, so switching between them changes a prop
  // and nothing else — React keeps the instance, and without `kind` in the
  // list nothing refetched: the URL said suppliers and the table went on
  // showing customers until the page was reloaded.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, kind]);

  // A search typed on one list must not silently filter the other.
  useEffect(() => {
    setQ("");
    setRows([]);
    setOpen(false);
    setEditing(null);
  }, [kind]);

  useEffect(() => {
    api.get("/masters/states").then(({ data }) => setStates(data));
    api.get("/price-lists").then(({ data }) => setPriceLists(data)).catch(() => {});
  }, []);

  const checkGstin = async (value) => {
    setForm((f) => ({ ...f, gstin: value.toUpperCase() }));
    if (value.length === 15) {
      const { data } = await api.get(`/masters/gstin/${value.toUpperCase()}`);
      setGstinInfo(data);
      if (data.valid) setForm((f) => ({ ...f, state_code: data.state_code }));
    } else setGstinInfo(null);
  };

  const save = async () => {
    if (!form.is_customer && !form.is_supplier) {
      toast.error("A party must be a customer, a supplier, or both");
      return;
    }
    try {
      const payload = { ...form, gstin: form.gstin || null,
        price_list_id: form.price_list_id || null,
        credit_period_days: Number(form.credit_period_days) || 0 };
      if (editing) await api.patch(`/parties/${editing.id}`, payload);
      else {
        const { data } = await api.post("/parties", payload);
        (data.warnings || []).forEach((w) => toast.warning(w));
      }
      toast.success(editing ? "Party updated" : "Party created");
      setOpen(false);
      setEditing(null);
      setForm(blank);
      setGstinInfo(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid={`parties-page-${kind.toLowerCase()}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{copy.title}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {rows.length} {copy.noun}{rows.length === 1 ? "" : "s"} · {copy.balanceLabel.toLowerCase()}{" "}
            <span className="font-mono">
              {inr(rows.reduce((s, r) => s + (kind === "SUPPLIER"
                ? Math.max(-r.current_balance, 0)
                : Math.max(r.current_balance, 0)), 0))}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PortButtons datasetKey={kind === "CUSTOMER" ? "customers" : "suppliers"} importable />
          {canEdit && (
            <Button data-testid="new-party-btn" onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
              className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
              <Plus size={16} /> New {copy.noun === "customer" ? "Customer" : "Supplier"}
            </Button>
          )}
        </div>
      </div>

      <div className="relative w-full sm:w-72">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="parties-search" className="pl-8 h-9" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${copy.noun}s by name, phone or GSTIN`} />
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="parties-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Name</th>
              <th className="px-3 py-2.5 text-left font-semibold">GSTIN</th>
              <th className="px-3 py-2.5 text-left font-semibold">State</th>
              <th className="px-3 py-2.5 text-left font-semibold">Phone</th>
              <th className="px-3 py-2.5 text-right font-semibold">Credit days</th>
              <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
              <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`party-row-${p.id}`}>
                <td className="px-4 py-2.5">
                  <button onClick={() => navigate(`/parties/${p.id}`)} className="font-medium hover:text-blue-700 transition-colors duration-150"
                    data-testid={`party-name-${p.id}`}>
                    {p.display_name}
                  </button>
                  <div className="text-[11px] text-slate-400">{p.registration_type}</div>
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px]">{p.gstin || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{p.state_name}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{p.phone}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{p.credit_period_days}</td>
                <td className={`px-3 py-2.5 text-right font-mono tabular-nums font-semibold ${p.current_balance > 0 ? "text-rose-700" : "text-emerald-700"}`}
                  data-testid={`party-balance-${p.id}`}>
                  {inr(p.current_balance)}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button className="text-slate-500 hover:text-blue-700 mr-3 transition-colors duration-150" title="Ledger"
                    data-testid={`party-ledger-${p.id}`} onClick={() => navigate(`/parties/${p.id}`)}>
                    <BookOpen size={15} />
                  </button>
                  <button className="text-slate-500 hover:text-blue-700 mr-3 transition-colors duration-150" title="New invoice"
                    data-testid={`party-invoice-${p.id}`} onClick={() => navigate(`/invoices/new?party_id=${p.id}`)}>
                    <FileText size={15} />
                  </button>
                  {canEdit && (
                    <button className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
                      data-testid={`party-edit-${p.id}`}
                      onClick={() => { setEditing(p); setForm({ ...blank, ...p }); setOpen(true); }}>
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-slate-500">No parties yet</td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty="No parties yet" testid="parties-cards">
        {rows.map((p) => (
          <DataCard
            key={p.id}
            testid={`party-card-${p.id}`}
            title={p.display_name}
            subtitle={`${p.registration_type} · ${p.state_name || "—"}`}
            right={
              <div className={`font-mono text-sm font-bold tabular-nums ${
                p.current_balance > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                {inr(p.current_balance)}
              </div>
            }
            rows={[
              ["Phone", p.phone || "—"],
              ["GSTIN", p.gstin || "—"],
              p.credit_period_days ? ["Credit days", p.credit_period_days] : null,
            ]}
            actions={
              <>
                <button className="text-xs font-medium text-blue-700"
                  data-testid={`party-ledger-m-${p.id}`}
                  onClick={() => navigate(`/parties/${p.id}`)}>Ledger</button>
                <button className="text-xs font-medium text-blue-700"
                  data-testid={`party-invoice-m-${p.id}`}
                  onClick={() => navigate(`/invoices/new?party_id=${p.id}`)}>New invoice</button>
                {canEdit && (
                  <button className="text-xs font-medium text-slate-600"
                    data-testid={`party-edit-m-${p.id}`}
                    onClick={() => { setEditing(p); setForm({ ...blank, ...p }); setOpen(true); }}>Edit</button>
                )}
              </>
            }
          />
        ))}
      </CardList>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="party-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit ${editing.display_name}`
                       : `New ${copy.noun === "customer" ? "customer" : "supplier"}`}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2 space-y-2 rounded-md border border-slate-200 p-3">
              <Label className="text-xs">Roles</Label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="accent-blue-600" data-testid="party-is-customer"
                    checked={!!form.is_customer}
                    onChange={(e) => setForm((f) => ({ ...f, is_customer: e.target.checked }))} />
                  Customer — you sell to them
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="accent-blue-600" data-testid="party-is-supplier"
                    checked={!!form.is_supplier}
                    onChange={(e) => setForm((f) => ({ ...f, is_supplier: e.target.checked }))} />
                  Supplier — you buy from them
                </label>
              </div>
              <p className="text-[11px] text-slate-500">
                Tick both for a business you both buy from and sell to. They stay one record with
                one ledger, so the two sides net off instead of showing as two balances.
              </p>
              {!form.is_customer && !form.is_supplier && (
                <p className="text-[11px] font-medium text-rose-600" data-testid="party-role-error">
                  Pick at least one role.
                </p>
              )}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Display name</Label>
              <Input data-testid="party-name-input" value={form.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">GSTIN (optional)</Label>
              <Input data-testid="party-gstin-input" className="font-mono uppercase" value={form.gstin || ""}
                onChange={(e) => checkGstin(e.target.value)} placeholder="27AABCW1234A1Z5" />
              {gstinInfo && (
                <div className={`text-[11px] ${gstinInfo.valid ? "text-emerald-700" : "text-rose-700"}`} data-testid="party-gstin-feedback">
                  {gstinInfo.valid ? `Valid · ${gstinInfo.state_name} · PAN ${gstinInfo.pan}` : gstinInfo.reason}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone</Label>
              <Input data-testid="party-phone-input" className="font-mono" value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+91 98220 11223" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input data-testid="party-email-input" value={form.email || ""}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Billing address</Label>
              <Input data-testid="party-address-input" value={form.billing_address || ""}
                onChange={(e) => setForm((f) => ({ ...f, billing_address: e.target.value, shipping_address: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">State</Label>
              <Select value={form.state_code} onValueChange={(v) => setForm((f) => ({ ...f, state_code: v, shipping_state_code: v }))}>
                <SelectTrigger data-testid="party-state-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name} ({s.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Credit period (days)</Label>
              <Input data-testid="party-credit-input" className="font-mono" value={form.credit_period_days}
                onChange={(e) => setForm((f) => ({ ...f, credit_period_days: e.target.value }))} />
            </div>
            {form.is_customer && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Price list</Label>
                <Select value={form.price_list_id || "NONE"}
                  onValueChange={(v) => setForm((f) => ({ ...f, price_list_id: v === "NONE" ? "" : v }))}>
                  <SelectTrigger data-testid="party-price-list-select">
                    <SelectValue placeholder="Standard prices" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Standard prices</SelectItem>
                    {priceLists.map((pl) => (
                      <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">
                  Their invoices start at this list's rates. Manage lists under Items → Price lists.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="party-cancel">Cancel</Button>
            <Button onClick={save} data-testid="party-save" className="bg-blue-600 hover:bg-blue-700">Save party</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
